/**
 * The document-type registry.
 *
 * One discriminated `documents` table, not eleven tables. Each entry declares
 * the label, the number series, the side, which predecessors it may be
 * created from, and whether it moves stock. Everything else — calc, renderer,
 * repository, conversion — reads from here, so adding a document type is one
 * entry rather than eleven code paths that can drift.
 *
 * That is the lesson from 1.6.x, where the invoice, quotation, customer PO
 * and vendor PO each had their own layout, and the invoice layout was wrong.
 */

export const DOC_TYPES = [
  // sales
  'QUOTATION',
  'PROFORMA',
  'SALES_ORDER',
  'DELIVERY_NOTE',
  'SALES_INVOICE',
  'CREDIT_NOTE',
  'SALES_RETURN',
  // purchase
  'PURCHASE_ORDER',
  'PURCHASE_RECEIPT',
  'PURCHASE_INVOICE',
  'DEBIT_NOTE',
  'PURCHASE_RETURN',
] as const;

export type DocType = (typeof DOC_TYPES)[number];
export type DocSide = 'SALES' | 'PURCHASE';

export interface DocTypeSpec {
  readonly type: DocType;
  readonly label: string;
  readonly shortLabel: string;
  readonly side: DocSide;
  /** default numbering series, e.g. INV / QTN / PO */
  readonly numberPrefix: string;
  /** A document that moves stock when issued. */
  readonly movesStock: boolean;
  readonly stockDirection: 'IN' | 'OUT' | null;
  /**
   * Document types this one may be created from. A Sales Invoice may come
   * from a Delivery Note OR straight from a Sales Order when the business
   * delivers-and-invoices in one step.
   */
  readonly convertibleFrom: readonly DocType[];
  /** A financial correction against an issued document. */
  readonly corrects: DocType | null;
  /**
   * This document is a TAX document: issuing it creates a GST liability and
   * therefore REQUIRES a resolved place of supply.
   *
   * False for a quotation, a proforma, an order and a delivery note. None of those
   * creates a tax liability, so demanding a place of supply for one would invent a
   * question the user never asked — and blocking a delivery on a field that does
   * not apply to it would stop real work for no reason.
   *
   * This lives in the document-type model rather than as a list inside a service.
   * A hard-coded list in a service is a second source of truth about what a
   * document IS, and it is exactly the kind of list that goes stale when the
   * eleventh document type is added.
   */
  readonly taxDocument: boolean;
}

const SPECS: Record<DocType, DocTypeSpec> = {
  QUOTATION: {
    taxDocument: false,
    type: 'QUOTATION',
    label: 'Quotation',
    shortLabel: 'Quotation',
    side: 'SALES',
    numberPrefix: 'QTN',
    movesStock: false,
    stockDirection: null,
    convertibleFrom: [],
    corrects: null,
  },
  /**
   * A proforma is a real document, not a malformed quotation: it estimates the
   * cost of goods or services BEFORE a final tax invoice exists. It is not a
   * tax document — it carries no GST obligation of its own and must never share
   * the invoice number series, or it would consume a legal invoice number.
   */
  PROFORMA: {
    taxDocument: false,
    type: 'PROFORMA',
    label: 'Proforma',
    shortLabel: 'PF',
    side: 'SALES',
    numberPrefix: 'PF',
    movesStock: false,
    stockDirection: null,
    convertibleFrom: ['QUOTATION'],
    corrects: null,
  },
  SALES_ORDER: {
    taxDocument: false,
    type: 'SALES_ORDER',
    label: 'Sales Order',
    shortLabel: 'SO',
    side: 'SALES',
    numberPrefix: 'SO',
    movesStock: false,
    stockDirection: null,
    // A 1.6.x customer PO and vendor PO both map here; provenance is kept on
    // the document as `sourceKind` rather than as a separate document type.
    convertibleFrom: ['QUOTATION'],
    corrects: null,
  },
  DELIVERY_NOTE: {
    taxDocument: false,
    type: 'DELIVERY_NOTE',
    label: 'Delivery Note',
    shortLabel: 'DN',
    side: 'SALES',
    numberPrefix: 'DN',
    movesStock: true,
    stockDirection: 'OUT',
    convertibleFrom: ['SALES_ORDER'],
    corrects: null,
  },
  SALES_INVOICE: {
    taxDocument: true,
    type: 'SALES_INVOICE',
    label: 'Tax Invoice',
    shortLabel: 'Invoice',
    side: 'SALES',
    numberPrefix: 'INV',
    movesStock: true,
    // Only when no Delivery Note exists — `stockService` checks the links so a
    // deliver-then-invoice sequence does not deduct twice.
    stockDirection: 'OUT',
    convertibleFrom: ['DELIVERY_NOTE', 'SALES_ORDER'],
    corrects: null,
  },
  CREDIT_NOTE: {
    taxDocument: true,
    type: 'CREDIT_NOTE',
    label: 'Credit Note',
    shortLabel: 'Credit Note',
    side: 'SALES',
    numberPrefix: 'CRN',
    movesStock: false,
    stockDirection: null,
    convertibleFrom: [],
    corrects: 'SALES_INVOICE',
  },
  SALES_RETURN: {
    taxDocument: true,
    type: 'SALES_RETURN',
    label: 'Sales Return',
    shortLabel: 'Sales Return',
    side: 'SALES',
    numberPrefix: 'SRN',
    // A return physically brings stock back, so it IS a stock movement.
    movesStock: true,
    stockDirection: 'IN',
    convertibleFrom: [],
    corrects: 'SALES_INVOICE',
  },
  PURCHASE_ORDER: {
    taxDocument: false,
    type: 'PURCHASE_ORDER',
    label: 'Purchase Order',
    shortLabel: 'PO',
    side: 'PURCHASE',
    numberPrefix: 'PO',
    movesStock: false,
    stockDirection: null,
    convertibleFrom: [],
    corrects: null,
  },
  PURCHASE_RECEIPT: {
    taxDocument: false,
    type: 'PURCHASE_RECEIPT',
    label: 'Purchase Receipt',
    shortLabel: 'GRN',
    side: 'PURCHASE',
    numberPrefix: 'GRN',
    movesStock: true,
    stockDirection: 'IN',
    convertibleFrom: ['PURCHASE_ORDER'],
    corrects: null,
  },
  PURCHASE_INVOICE: {
    taxDocument: true,
    type: 'PURCHASE_INVOICE',
    label: 'Purchase Invoice',
    shortLabel: 'Bill',
    side: 'PURCHASE',
    numberPrefix: 'PBILL',
    movesStock: true,
    stockDirection: 'IN',
    convertibleFrom: ['PURCHASE_RECEIPT', 'PURCHASE_ORDER'],
    corrects: null,
  },
  DEBIT_NOTE: {
    taxDocument: true,
    type: 'DEBIT_NOTE',
    label: 'Debit Note',
    shortLabel: 'Debit Note',
    side: 'PURCHASE',
    numberPrefix: 'DBN',
    movesStock: false,
    stockDirection: null,
    convertibleFrom: [],
    corrects: 'PURCHASE_INVOICE',
  },
  PURCHASE_RETURN: {
    taxDocument: true,
    type: 'PURCHASE_RETURN',
    label: 'Purchase Return',
    shortLabel: 'Purchase Return',
    side: 'PURCHASE',
    numberPrefix: 'PRN',
    movesStock: true,
    stockDirection: 'OUT',
    convertibleFrom: [],
    corrects: 'PURCHASE_INVOICE',
  },
};

export function docSpec(type: DocType): DocTypeSpec {
  const spec = SPECS[type];
  if (!spec) throw new Error(`Unknown document type: ${String(type)}`);
  return spec;
}

export function isDocType(value: unknown): value is DocType {
  return typeof value === 'string' && value in SPECS;
}

export function docTypesOnSide(side: DocSide): readonly DocType[] {
  return DOC_TYPES.filter((t) => SPECS[t].side === side);
}

export function isSalesDoc(type: DocType): boolean {
  return SPECS[type].side === 'SALES';
}

export function isPurchaseDoc(type: DocType): boolean {
  return SPECS[type].side === 'PURCHASE';
}

/** True when `from` may be converted into `to`. */
export function canConvert(from: DocType, to: DocType): boolean {
  return SPECS[to].convertibleFrom.includes(from);
}

/** The party a document is raised against, by side. */
export function counterpartyField(type: DocType): 'customerId' | 'supplierId' {
  return SPECS[type].side === 'SALES' ? 'customerId' : 'supplierId';
}
